export type AnalyticsSurface =
  | 'book_page'
  | 'docked_player'
  | 'library'
  | 'my_books'
  | 'pricing_modal'
  | 'pricing_page'
  | 'reader';

/** Surfaces that own a player, and so can report a listening position. */
export type PlayerSurface = Extract<AnalyticsSurface, 'docked_player' | 'reader'>;

/** Surfaces a voice session can be started from. */
export type VoiceEntrySurface = PlayerSurface | Extract<AnalyticsSurface, 'book_page'>;

export type AnalyticsInputMethod =
  | 'mouse'
  | 'touch'
  | 'keyboard'
  | 'voice'
  | 'automatic'
  | 'unknown';

export type AnalyticsTrigger =
  | 'button'
  | 'swipe'
  | 'keyboard'
  | 'automatic'
  | 'deep_link'
  | 'voice_command'
  | 'transport_disconnect'
  | 'timeout'
  | 'error'
  | 'navigation'
  // A tap on a conversation's dot in the reader's margin.
  | 'margin_dot';

export interface InteractionContext {
  surface: AnalyticsSurface;
  source?: string;
  trigger?: AnalyticsTrigger;
  input_method?: AnalyticsInputMethod;
}

export type VoiceEndReason =
  | 'user_stop'
  | 'disconnect'
  | 'timeout'
  | 'error'
  | 'navigation'
  | 'usage_limit';

export interface VoiceStartContext extends InteractionContext {
  surface: VoiceEntrySurface;
}

export interface VoiceEndContext extends VoiceStartContext {
  end_reason: VoiceEndReason;
}

type ReactLikeEvent = {
  nativeEvent?: Event;
};

export const inputMethodFromEvent = (
  event?: ReactLikeEvent,
): AnalyticsInputMethod => {
  const nativeEvent = event?.nativeEvent;
  if (!nativeEvent) return 'unknown';

  if (typeof KeyboardEvent !== 'undefined' && nativeEvent instanceof KeyboardEvent) {
    return 'keyboard';
  }

  if (typeof PointerEvent !== 'undefined' && nativeEvent instanceof PointerEvent) {
    if (nativeEvent.pointerType === 'touch' || nativeEvent.pointerType === 'pen') {
      return 'touch';
    }
    if (nativeEvent.pointerType === 'mouse') return 'mouse';
  }

  if (typeof MouseEvent !== 'undefined' && nativeEvent instanceof MouseEvent) {
    if (nativeEvent.detail === 0) return 'keyboard';
    const capabilities = (nativeEvent as MouseEvent & {
      sourceCapabilities?: { firesTouchEvents?: boolean };
    }).sourceCapabilities;
    return capabilities?.firesTouchEvents ? 'touch' : 'mouse';
  }

  if (typeof TouchEvent !== 'undefined' && nativeEvent instanceof TouchEvent) {
    return 'touch';
  }

  return 'unknown';
};
